// Bridge-build affordance: when an engineer is selected, ring the adjacent
// tiles that sit across a river edge he can bridge. Click a ring to build.

import type { SceneNode } from '@aod/protocol';
import { useMemo } from 'react';
import * as THREE from 'three';
import { RenderLayer } from './RenderLayer.js';
import { tileElevationAt } from './elevation.js';

interface BridgeTarget {
  readonly q: number;
  readonly r: number;
  readonly position: [number, number, number];
}

export function isEngineer(unit: SceneNode): boolean {
  return (unit.props as Record<string, unknown> | undefined)?.kind === 'engineer';
}

/** Adjacent river-edge tiles the engineer could bridge (not already bridged). */
export function bridgeTargets(
  unit: SceneNode,
  tiles: ReadonlyArray<SceneNode>,
  biomeMap: Map<string, string>,
): BridgeTarget[] {
  const props = unit.props as Record<string, unknown> | undefined;
  const q = (props?.q as number) ?? 0;
  const r = (props?.r as number) ?? 0;
  const key = `${q},${r}`;
  const origin = tiles.find((t) => {
    const p = t.props as Record<string, unknown> | undefined;
    return `${p?.q},${p?.r}` === key;
  });
  if (!origin) return [];
  const originEdges = Number((origin.props as Record<string, unknown>)?.riverEdges ?? 0);
  const originBridges = Number((origin.props as Record<string, unknown>)?.bridgeEdges ?? 0);
  if (!originEdges) return [];

  // Direction i from a pointy-top hex at axialToWorld: neighbours differ by
  // AXIAL_DIRS — the snapshot edge bits use the same 0..5 order as the sim's
  // AXIAL_DIRS (E, NE, NW, W, SW, SE).
  const DIRS: ReadonlyArray<readonly [number, number]> = [
    [1, 0],
    [1, -1],
    [0, -1],
    [-1, 0],
    [-1, 1],
    [0, 1],
  ];
  const out: BridgeTarget[] = [];
  DIRS.forEach(([dq, dr], dir) => {
    if ((originEdges & (1 << dir)) === 0) return;
    if ((originBridges & (1 << dir)) !== 0) return; // already bridged
    const nq = q + dq;
    const nr = r + dr;
    const nKey = `${nq},${nr}`;
    const node = tiles.find((t) => {
      const p = t.props as Record<string, unknown> | undefined;
      return `${p?.q},${p?.r}` === nKey;
    });
    if (!node) return;
    const nProps = node.props as Record<string, unknown>;
    if ((Number(nProps?.bridgeEdges ?? 0) & (1 << ((dir + 3) % 6))) !== 0) return; // symmetric already-bridged
    const biome = biomeMap.get(nKey) ?? 'plains';
    if (biome === 'ocean' || biome === 'coast' || biome === 'river') return;
    out.push({
      q: nq,
      r: nr,
      position: [
        node.transform.position[0],
        tileElevationAt(nq, nr, biomeMap) + 0.05,
        node.transform.position[2],
      ],
    });
  });
  return out;
}

export function BridgeTargetRings({
  unit,
  tiles,
  biomeMap,
  onBuild,
}: {
  readonly unit: SceneNode;
  readonly tiles: ReadonlyArray<SceneNode>;
  readonly biomeMap: Map<string, string>;
  readonly onBuild?: (unitId: string, q: number, r: number) => void;
}) {
  const targets = useMemo(() => bridgeTargets(unit, tiles, biomeMap), [unit, tiles, biomeMap]);
  const ringGeo = useMemo(() => new THREE.TorusGeometry(0.42, 0.045, 8, 32), []);
  const ringMat = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: new THREE.Color('#c98b3a'),
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
      }),
    [],
  );
  if (targets.length === 0) return null;
  return (
    <group>
      {targets.map((t) => (
        <mesh
          key={`${t.q},${t.r}`}
          geometry={ringGeo}
          material={ringMat}
          position={t.position}
          rotation={[-Math.PI / 2, 0, 0]}
          renderOrder={RenderLayer.UI - 1}
          onClick={(e) => {
            e.stopPropagation();
            const unitId = unit.id;
            onBuild?.(unitId, t.q, t.r);
          }}
        />
      ))}
    </group>
  );
}
